/**
 * The "physical lines" of a booking for pickup / return checklists: every
 * booking item except bundle parents (a bundle parent has no inventory; its
 * components are the lines that get handed over).
 */

export interface PhysicalLine {
  id: string;
  product_type_id: string;
  inventory_item_id: string | null;
  size: string | null;
  item_name: string;
  quantity: number;
  unit_label: string | null;
  /** From the line's product; null when unknown. */
  inventory_type: 'individual' | 'pool' | 'unlimited' | null;
  /** Bundle component (has a parent line). */
  in_bundle: boolean;
}

export function physicalLines(items: any[] | null | undefined): PhysicalLine[] {
  return (items || [])
    .filter((item) => !item.is_bundle_parent)
    .map((item) => ({
      id: item.id,
      product_type_id: item.product_type_id,
      inventory_item_id: item.inventory_item_id ?? null,
      size: item.size ?? null,
      item_name: item.item_name || item.product_types?.product_name || '',
      quantity: item.inventory_item_id ? 1 : Math.max(1, item.quantity || 1),
      unit_label: item.inventory_items
        ? item.inventory_items.item_number || (item.inventory_items.nr != null ? `#${item.inventory_items.nr}` : null)
        : null,
      inventory_type: item.product_types?.inventory_type ?? (item.inventory_item_id ? 'individual' : null),
      in_bundle: !!item.parent_booking_item_id,
    }));
}

/** An individual-inventory line that has no unit allocated yet. */
export function isUnallocatedUnitLine(line: PhysicalLine): boolean {
  return !line.inventory_item_id && line.inventory_type === 'individual';
}

export function lineLabel(line: PhysicalLine): string {
  const base = `${line.item_name}${line.size ? ` (${line.size})` : ''}`;
  if (line.unit_label) return `${base} · ${line.unit_label}`;
  if (line.quantity > 1) return `${line.quantity}× ${base}`;
  return base;
}

/** Lines whose tick count is below their quantity. */
export function untickedLines(lines: PhysicalLine[], ticks: Record<string, number>): PhysicalLine[] {
  return lines.filter((line) => (ticks[line.id] || 0) < line.quantity);
}
